import { groupKeyFor, measureAmount } from "@/components/properties/grouping";

/**
 * PR/GS keys follow the "Material Code_System Code" naming (e.g. 1OS6856.XX_P1-PR-9):
 * the part before the underscore is the Material Code used for consolidation, so
 * every system-code variant of the same material merges into one row — across all
 * selected panel types. Keys without an underscore consolidate under their full key.
 */
const materialCodeOf = (category, key) => {
  if (category !== "extrusions" && category !== "gaskets") return key;
  const index = key.indexOf("_");
  return index > 0 ? key.slice(0, index).trim() : key;
};

export const MATERIAL_CATEGORIES = [
  { key: "extrusions", label: "Extrusions" },
  { key: "gaskets", label: "Gaskets" },
  { key: "fasteners", label: "Fasteners" },
  { key: "anchors", label: "Anchors" },
  { key: "other", label: "Other" },
  { key: "glass", label: "Glass" },
];

const ALL_CATEGORIES = MATERIAL_CATEGORIES.map((c) => c.key);

/** Order type → material categories it includes. Complete Panel (and legacy/unknown
 *  types) include everything. */
const TYPE_CATEGORIES = {
  glass: ["glass"],
  extrusions: ["extrusions"],
  gaskets: ["gaskets"],
  fasteners: ["fasteners"],
  extrusion: ["extrusions"], // legacy key
  fastener: ["fasteners"], // legacy key
};

export function categoriesForOrderTypes(orderTypes) {
  const types = orderTypes || [];
  if (!types.length || types.includes("complete_panel")) return new Set(ALL_CATEGORIES);
  const allowed = new Set();
  types.forEach((type) =>
    (TYPE_CATEGORIES[type] || ALL_CATEGORIES).forEach((key) => allowed.add(key))
  );
  return allowed;
}

/**
 * Consolidated material requirements for an order, grouped by the same categories
 * used in Panel Properties. Per selected panel type: panel count × every measurable
 * key/value of that block definition (merged model+manual props + manual group
 * elements); glass records count once per panel. Materials consolidate by key —
 * quantity-based and length-based values are never combined (separate entries).
 */
export function buildMaterialList(summary, defsByName, orderTypes) {
  const allowed = categoriesForOrderTypes(orderTypes);
  const materials = new Map(); // `${category}:${kind}:${key}`
  const add = (category, kind, key, amount) => {
    const id = `${category}:${kind}:${key}`;
    const material = materials.get(id) || { key, kind, total: 0 };
    material.total += amount;
    materials.set(id, material);
  };

  (summary || []).forEach(({ block_definition, count }) => {
    const definition = defsByName.get(block_definition);
    if (!definition) return;

    Object.entries(definition.properties || {}).forEach(([key, value]) => {
      const measure = measureAmount(value);
      if (measure.kind === "text") return;
      const category = groupKeyFor(key);
      add(category, measure.kind, materialCodeOf(category, key), measure.amount * count);
    });

    Object.entries(definition.elements_manual || {}).forEach(([group, elements]) =>
      (elements || []).forEach((element) => {
        const measure = measureAmount(element.value);
        if (measure.kind === "text") return;
        add(group, measure.kind, materialCodeOf(group, element.name), measure.amount * count);
      })
    );

    (definition.glass || []).forEach((entry) => {
      add("glass", "quantity", entry.code || entry.combination || "Glass", count);
    });
  });

  const byCategory = {};
  MATERIAL_CATEGORIES.forEach(({ key }) => {
    byCategory[key] = [];
  });
  materials.forEach((material, id) => {
    const category = id.split(":", 1)[0];
    if (!allowed.has(category)) return;
    (byCategory[category] || byCategory.other).push(material);
  });
  Object.values(byCategory).forEach((list) =>
    list.sort((a, b) => b.total - a.total || a.key.localeCompare(b.key))
  );
  return byCategory;
}
